/* Email subject line tester: inbox rows drawn from measured text, plus plain
   checks. Pure functions are exported for tests (and for the server render);
   in a browser the same file initializes the page. Nothing leaves the page. */
(function (root, factory) {
  'use strict';
  const api = factory();
  const commonJS = typeof module === 'object' && module && module.exports;
  if (commonJS) module.exports = api;
  else if (root) root.SubjectLineTester = api;
  if (!commonJS && typeof document !== 'undefined') {
    const start = () => api.init(document.querySelector('[data-slt]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const ELLIPSIS = '…';
  const TIME = '9:41 AM';
  // Fewer than this many characters of empty space is not worth a warning:
  // the app has too little room to show more than a letter or two of the email.
  const ROOM_CHARS = 4;
  // Everyday copy for turning a leftover width into "about N characters".
  const SAMPLE = 'Take another look at the item you left in your cart. We saved it for you.';

  const unicodePattern = (source, flags, fallback) => {
    try { return new RegExp(source, flags); } catch (_) { return fallback; }
  };
  const EMOJI = unicodePattern('\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\ufe0f|\\p{Regional_Indicator}|\\u20e3|\\p{Emoji_Modifier}', 'u', /[\ud83c-\ud83e][\udc00-\udfff]|[\u2600-\u27bf]\ufe0f/);
  // Text-style symbols that stand in for graphics in a sender name, such as
  // "Solara ✔" or "Solara ★". ©, ® and ™ are pictographs to Unicode too, but
  // they belong to real brand names.
  const SYMBOL = unicodePattern('(?![\u00a9\u00ae\u2122])\\p{Extended_Pictographic}|[\\u2600-\\u27bf\\u2b00-\\u2bff]', 'u', /[\u2600-\u27bf\u2b00-\u2bff]|[\ud83c-\ud83e][\udc00-\udfff]/);
  const WORD = unicodePattern('[\\p{L}\\p{N}][\\p{L}\\p{N}\'’.\\-]*[\\p{L}\\p{N}]|[\\p{L}\\p{N}]', 'gu', /[A-Za-z0-9\u00c0-\u024f][A-Za-z0-9\u00c0-\u024f'’.-]*[A-Za-z0-9\u00c0-\u024f]|[A-Za-z0-9\u00c0-\u024f]/g);
  const LETTERS = unicodePattern('\\p{L}', 'gu', /[A-Za-z\u00c0-\u024f]/g);
  const DIGIT = unicodePattern('\\p{N}', 'u', /[0-9]/);
  const segmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;

  // Each app's own typeface comes first; the fallbacks are the closest ones a
  // visitor's device is likely to have, measured and drawn with the same stack.
  const STACKS = {
    google: "'Google Sans', Roboto, Arial, sans-serif",
    roboto: "Roboto, 'Google Sans Text', Arial, sans-serif",
    apple: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    segoe: "'Segoe UI', 'Segoe UI Web (West European)', 'Helvetica Neue', Arial, sans-serif"
  };
  const font = (weight, size, family) => Object.freeze({ weight, size, family });

  // Widths are in CSS pixels at each app's default text size. They are set so
  // everyday text cuts close to published counts: about 41 subject characters
  // on an iPhone and up to 70 in Gmail on a computer (Campaign Monitor), 35-40
  // subject and about 84 preview characters in iPhone Mail (Litmus, Email on
  // Acid), and about 50 preview characters in Outlook (Email on Acid).
  // `short` names the app in one-line answers; `phone` marks the phone apps.
  const CLIENTS = Object.freeze([
    Object.freeze({
      id: 'gmailWeb', name: 'Gmail on a computer', short: 'Gmail on a computer', phone: false, setting: 'Laptop-size browser window, side menu open',
      kind: 'line', frame: 848, lineWidth: 480, senderWidth: 168, separator: ' - ', noSubject: '(no subject)',
      fonts: Object.freeze({ sender: font(700, 14, STACKS.google), subject: font(700, 14, STACKS.google), preview: font(400, 14, STACKS.google), time: font(700, 12, STACKS.google) })
    }),
    Object.freeze({
      id: 'gmailApp', name: 'Gmail app on a phone', short: 'Gmail app', phone: true, setting: 'Small phone, default text size',
      kind: 'rows', frame: 375, textWidth: 287, previewWidth: 255, previewLines: 1, timeOn: 'sender', noSubject: '(no subject)',
      fonts: Object.freeze({ sender: font(700, 16, STACKS.roboto), subject: font(700, 14, STACKS.roboto), preview: font(400, 14, STACKS.roboto), time: font(700, 12, STACKS.roboto) })
    }),
    Object.freeze({
      id: 'appleMail', name: 'Apple Mail on iPhone', short: 'Apple Mail', phone: true, setting: 'Small iPhone, 2 preview lines (the default)',
      kind: 'rows', frame: 375, textWidth: 280, previewLines: 2, timeOn: 'sender', noSubject: 'No Subject',
      fonts: Object.freeze({ sender: font(600, 17, STACKS.apple), subject: font(400, 15, STACKS.apple), preview: font(400, 15, STACKS.apple), time: font(400, 15, STACKS.apple) })
    }),
    Object.freeze({
      id: 'outlook', name: 'Outlook on a computer', short: 'Outlook on a computer', phone: false, setting: 'New Outlook, reading pane on the right',
      kind: 'rows', frame: 370, textWidth: 290, previewLines: 1, timeOn: 'subject', noSubject: '(No subject)',
      fonts: Object.freeze({ sender: font(600, 14, STACKS.segoe), subject: font(600, 14, STACKS.segoe), preview: font(400, 13, STACKS.segoe), time: font(400, 12, STACKS.segoe) })
    })
  ]);
  const GAP = 8;

  // The Solara examples from the guides. Two carry a name tag: one without a
  // fallback (the check catches it) and one with a fallback (the check passes).
  const EXAMPLES = Object.freeze({
    cart: Object.freeze({ label: 'Cart reminder', subject: 'Your Daily Cleanser is still here', preview: 'Take another look at the item you left in your cart.' }),
    welcome: Object.freeze({ label: 'Welcome', subject: '{{ first_name }}, welcome to Solara! Here’s your skincare guide', preview: 'Start with three small steps for your daily routine.' }),
    winback: Object.freeze({ label: 'Win-back', subject: "Hi {{ first_name|default:'there' }}, it’s been a while", preview: 'Reorder your Daily Cleanser, or meet the new Travel Cleanser.' }),
    product: Object.freeze({ label: 'Product news', subject: 'Your favorite cleanser, in a smaller size', preview: 'Meet the small bottle for the times you want to carry less.' })
  });
  const START = Object.freeze({ from: 'Solara', example: 'cart' });

  const esc = value => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const number = value => value.toLocaleString('en-US');
  const plural = (count, one, many = `${one}s`) => `${number(count)} ${count === 1 ? one : many}`;
  const joinWords = words => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
  const collapse = text => String(text || '').replace(/\s+/g, ' ').trim();
  const isSpace = char => /^\s$/.test(char);

  function graphemes(text) {
    const value = String(text || '');
    return segmenter ? Array.from(segmenter.segment(value), part => part.segment) : Array.from(value);
  }
  const countCharacters = text => graphemes(text).length;

  function fontString(spec) {
    return `${spec.weight} ${spec.size}px ${spec.family}`;
  }

  // ---- Measuring -----------------------------------------------------------

  // Arial advance widths (1/1000 em) for printable ASCII. Used to draw the page
  // before the browser measures, and as a deterministic stand-in in tests.
  const ASCII = [278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584];
  const TYPOGRAPHIC = { '‘': 222, '’': 222, '“': 333, '”': 333, '–': 556, '—': 1000, '…': 1000, '•': 350, '€': 556, '£': 556, '©': 737, '®': 737, '™': 1000, '\u00a0': 278 };

  function glyphWidth(part) {
    if (EMOJI.test(part)) return 1230;
    if (TYPOGRAPHIC[part] !== undefined) return TYPOGRAPHIC[part];
    const code = part.codePointAt(0);
    if (code >= 32 && code <= 126 && part.length === 1) return ASCII[code - 32];
    const base = part.normalize('NFD').charAt(0);
    const baseCode = base.charCodeAt(0);
    if (baseCode >= 32 && baseCode <= 126) return ASCII[baseCode - 32];
    if (code >= 0x2e80) return 1000;
    return 556;
  }

  function approximateMeasure(text, spec) {
    let units = 0;
    graphemes(text).forEach(part => { units += glyphWidth(part); });
    const weight = spec.weight >= 700 ? 1.07 : spec.weight >= 600 ? 1.04 : 1;
    const family = /apple-system/.test(spec.family) ? 1.03 : 1;
    return units / 1000 * spec.size * weight * family;
  }

  function canvasMeasure(doc) {
    try {
      const canvas = doc && doc.createElement('canvas');
      const context = canvas && canvas.getContext && canvas.getContext('2d');
      if (!context || typeof context.measureText !== 'function') return null;
      const cache = new Map();
      let current = '';
      return (text, spec) => {
        const css = fontString(spec);
        const key = `${css}\u0000${text}`;
        const known = cache.get(key);
        if (known !== undefined) return known;
        if (css !== current) { context.font = css; current = css; }
        const width = context.measureText(text).width;
        if (cache.size > 4000) cache.clear();
        cache.set(key, width);
        return width;
      };
    } catch (_) {
      return null;
    }
  }

  // ---- Fitting text into a width -------------------------------------------

  // Fits runs of differently styled text (Gmail's bold subject, " - ", then the
  // preview) into one line. When they do not fit, the line ends in "…" after
  // the most graphemes that fit, the way CSS and phone apps truncate a line.
  function fitRuns(runs, width, measure) {
    const parts = runs.map(run => ({ font: run.font, chars: graphemes(run.text) }));
    const full = parts.map(part => (part.chars.length ? measure(part.chars.join(''), part.font) : 0));
    const count = parts.reduce((sum, part) => sum + part.chars.length, 0);
    const total = full.reduce((sum, value) => sum + value, 0);
    if (total <= width + 0.01) {
      return { texts: parts.map(part => part.chars.join('')), cut: false, used: total, room: Math.max(0, width - total), shown: count, count, ellipsisRun: -1 };
    }
    const prefix = size => {
      let left = size;
      const kept = parts.map(part => {
        const take = Math.min(left, part.chars.length);
        left -= take;
        return part.chars.slice(0, take);
      });
      // A line never ends on a space before its "…".
      let shown = size;
      for (let index = kept.length - 1; index >= 0; index--) {
        while (kept[index].length && isSpace(kept[index][kept[index].length - 1])) { kept[index].pop(); shown--; }
        if (kept[index].length) break;
      }
      let last = -1;
      kept.forEach((chars, index) => { if (chars.length) last = index; });
      return { kept, shown, last };
    };
    const widthOf = candidate => {
      let used = 0;
      candidate.kept.forEach((chars, index) => {
        if (!chars.length) return;
        used += chars.length === parts[index].chars.length ? full[index] : measure(chars.join(''), parts[index].font);
      });
      return used + measure(ELLIPSIS, parts[Math.max(0, candidate.last)].font);
    };
    let low = 0;
    let high = count - 1;
    let best = null;
    while (low <= high) {
      const middle = (low + high) >> 1;
      const candidate = prefix(middle);
      const used = widthOf(candidate);
      if (used <= width + 0.01) { best = { candidate, used }; low = middle + 1; } else high = middle - 1;
    }
    if (!best) return { texts: parts.map(() => ''), cut: true, used: 0, room: 0, shown: 0, count, ellipsisRun: -1 };
    return {
      texts: best.candidate.kept.map(chars => chars.join('')), cut: true, used: best.used, room: 0,
      shown: best.candidate.shown, count, ellipsisRun: Math.max(0, best.candidate.last)
    };
  }

  function fitText(text, width, spec, measure) {
    const fit = fitRuns([{ text, font: spec }], width, measure);
    return { text: fit.texts[0], cut: fit.cut, used: fit.used, room: fit.room, shown: fit.shown, count: fit.count };
  }

  // Wraps text over a few lines at spaces (or after a hyphen), breaking inside
  // a word only when one word is wider than the line. The last line ends in
  // "…" if text is left over, as Apple Mail's two preview lines do.
  function wrapText(text, width, maxLines, spec, measure) {
    const chars = graphemes(text);
    const lines = [];
    let start = 0;
    let cut = false;
    let lastWidth = 0;
    const lineWidth = parts => measure(parts.join('').replace(/\s+$/, ''), spec);
    while (start < chars.length && lines.length < maxLines) {
      const rest = chars.slice(start);
      if (lines.length === maxLines - 1) {
        const fit = fitText(rest.join(''), width, spec, measure);
        lines.push({ text: fit.text, cut: fit.cut });
        lastWidth = fit.used;
        cut = fit.cut;
        start += fit.cut ? fit.shown : rest.length;
        break;
      }
      let low = 1;
      let high = rest.length;
      let fits = 0;
      while (low <= high) {
        const middle = (low + high) >> 1;
        if (lineWidth(rest.slice(0, middle)) <= width + 0.01) { fits = middle; low = middle + 1; } else high = middle - 1;
      }
      if (fits >= rest.length) {
        const textLine = rest.join('').replace(/\s+$/, '');
        lines.push({ text: textLine, cut: false });
        lastWidth = measure(textLine, spec);
        start = chars.length;
        break;
      }
      let breakAt = Math.max(1, fits);
      if (!isSpace(rest[breakAt])) {
        let index = breakAt - 1;
        while (index > 0 && !isSpace(rest[index]) && rest[index] !== '-') index--;
        if (index > 0) breakAt = rest[index] === '-' ? index + 1 : index;
      }
      lines.push({ text: rest.slice(0, breakAt).join('').replace(/\s+$/, ''), cut: false });
      let next = breakAt;
      while (next < rest.length && isSpace(rest[next])) next++;
      start += next;
    }
    const shown = cut ? start : chars.length;
    const emptyLines = Math.max(0, maxLines - lines.length);
    const lastRoom = lines.length && !cut ? Math.max(0, width - lastWidth) : 0;
    return { lines, cut, shown, count: chars.length, lastRoom, emptyLines, room: cut ? 0 : lastRoom + emptyLines * width };
  }

  // ---- Personalization tags ------------------------------------------------

  // {{ first_name }}, {{ person.first_name|default:'there' }}, *|FNAME|*,
  // %%FirstName%%, [[FIRSTNAME OR "there"]], {first_name} and %FIRSTNAME%.
  // Single braces count only around a name field: "Our {new} cleanser" is text.
  const TAG_SOURCE = '\\{\\{\\s*([^{}]*?)\\s*\\}\\}|\\*\\|\\s*([A-Za-z0-9_]+)\\s*\\|\\*|%%\\s*([A-Za-z_][\\w.]*)\\s*%%|\\[\\[\\s*([^\\[\\]]+?)\\s*\\]\\]|\\{\\s*([A-Za-z_][\\w.-]*)\\s*\\}|%([A-Z][A-Z0-9_]*)%';
  // Template logic: {% if first_name %}…{% else %}…{% endif %} (Liquid,
  // Klaviyo) and *|IF:FNAME|*…*|ELSE:|*…*|END:IF|* (Mailchimp). Group 1 or 2
  // holds the keyword.
  const LOGIC_SOURCE = '\\{%-?\\s*(\\w+)[^%]*%\\}|\\*\\|\\s*(IF|IFNOT|ELSEIF|ELSE|END)\\s*:[^|]*\\|\\*';
  const LOGIC = new RegExp(LOGIC_SOURCE, 'i');
  const OPENS = new Set(['if', 'unless', 'ifnot']);
  const CLOSES = new Set(['endif', 'endunless', 'end']);
  const FIELD_PREFIX = /^(?:person|customer|contact|subscriber|recipient|profile|user|shopper|lead|member)_+/;
  const NAME_KINDS = new Set(['first name', 'last name', 'name']);

  const hasLogic = text => LOGIC.test(String(text || ''));
  const logicWord = match => String(match[1] || match[2] || '').toLowerCase();

  // Stretches of text whose showing depends on a condition. A tag inside one
  // is already handled by the condition, so it gets no fallback warning.
  function conditionSpans(text) {
    const value = String(text || '');
    const spans = [];
    if (!hasLogic(value)) return spans;
    const pattern = new RegExp(LOGIC_SOURCE, 'gi');
    let depth = 0;
    let start = 0;
    let match;
    while ((match = pattern.exec(value))) {
      const word = logicWord(match);
      if (OPENS.has(word)) {
        if (!depth) start = match.index;
        depth++;
      } else if (CLOSES.has(word) && depth) {
        depth--;
        if (!depth) spans.push([start, match.index + match[0].length]);
      }
    }
    if (depth) spans.push([start, value.length]);
    return spans;
  }

  // This page doesn't run conditions. For a reader with a sample name it shows
  // the first part of each one; for a reader with nothing on file, the
  // {% else %} part or nothing ({% unless %} the other way round). Other logic
  // tags, such as {% for %}, are dropped and the text between them kept.
  function resolveLogic(text, mode) {
    const value = String(text || '');
    if (!hasLogic(value)) return value;
    const pattern = new RegExp(LOGIC_SOURCE, 'gi');
    const open = [];
    const showing = () => open.every(frame => frame.show);
    let output = '';
    let at = 0;
    let match;
    while ((match = pattern.exec(value))) {
      if (showing()) output += value.slice(at, match.index);
      at = match.index + match[0].length;
      const word = logicWord(match);
      const frame = open[open.length - 1];
      if (OPENS.has(word)) {
        const show = (mode === 'empty') === (word !== 'if');
        open.push({ show, taken: show });
      } else if (frame && (word === 'elsif' || word === 'elif' || word === 'elseif')) {
        frame.show = false;
      } else if (frame && word === 'else') {
        frame.show = !frame.taken;
        frame.taken = true;
      } else if (frame && CLOSES.has(word)) {
        open.pop();
      }
    }
    if (showing()) output += value.slice(at);
    return output;
  }

  function findTags(text) {
    const pattern = new RegExp(TAG_SOURCE, 'g');
    const tags = [];
    const value = String(text || '');
    const spans = conditionSpans(value);
    let match;
    while ((match = pattern.exec(value))) {
      const index = [1, 2, 3, 4, 5, 6].find(group => match[group] !== undefined);
      const inner = match[index].trim();
      if (!inner) continue;
      let field = inner;
      let fallback = null;
      if (index === 1) {
        field = inner.split('|')[0].trim();
        const found = /\|\s*default\s*(?::|\()\s*(['"])(.*?)\1/i.exec(inner);
        if (found) fallback = found[2];
      } else if (index === 4) {
        field = inner.split(/\s+OR\s+/i)[0].trim();
        const found = /\s+OR\s+(['"])(.*?)\1/i.exec(inner);
        if (found) fallback = found[2];
      }
      if (!field) continue;
      if (index === 5 && !NAME_KINDS.has(fieldKind(field))) continue;
      const start = match.index;
      tags.push({
        text: match[0], start, end: start + match[0].length, field, fallback,
        mailchimp: index === 2, guarded: spans.some(([from, to]) => start >= from && start < to)
      });
    }
    return tags;
  }

  function fieldKind(field) {
    const key = String(field).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(FIELD_PREFIX, '');
    if (/(^|_)(first_?name|fname|given_?name)(_|$)/.test(key) || key === 'first') return 'first name';
    if (/(^|_)(last_?name|lname|surname|family_?name)(_|$)/.test(key)) return 'last name';
    if (/^(full_?)?name$|display_?name/.test(key)) return 'name';
    if (/city/.test(key)) return 'city';
    if (/product|item/.test(key)) return 'product';
    if (/store|shop|company|organi[sz]ation|brand/.test(key)) return 'store';
    if (/e_?mail/.test(key)) return 'email';
    return key.replace(/_/g, ' ').trim() || 'detail';
  }

  const SAMPLES = { 'first name': 'Maya', 'last name': 'Lee', name: 'Maya Lee', city: 'Austin', product: 'Daily Cleanser', email: 'maya@example.com' };

  function sampleValue(field, from) {
    const kind = fieldKind(field);
    if (kind === 'store') return collapse(from) || 'Solara';
    return SAMPLES[kind] || `[${kind}]`;
  }

  // mode 'sample': a made-up reader (Maya). mode 'empty': a reader with nothing
  // on file, so each tag becomes its fallback or nothing at all. A store tag
  // such as {{ shop.name }} is never empty: it takes the From name.
  function fillTags(text, mode, from) {
    const value = resolveLogic(text, mode);
    const tags = findTags(value);
    if (!tags.length) return value;
    let output = '';
    let at = 0;
    tags.forEach(tag => {
      const empty = mode === 'empty' && fieldKind(tag.field) !== 'store';
      output += value.slice(at, tag.start) + (empty ? (tag.fallback || '') : sampleValue(tag.field, from));
      at = tag.end;
    });
    return output + value.slice(at);
  }

  // Tags that change from reader to reader: everything but the store's own.
  const readerTags = text => findTags(text).filter(tag => fieldKind(tag.field) !== 'store');

  // ---- Text checks ---------------------------------------------------------

  function words(text) {
    const value = String(text || '');
    return value.match(new RegExp(WORD.source, WORD.flags)) || [];
  }

  function countWords(text) {
    // A tag such as {{ first_name }} is one word however it is spaced.
    return words(String(text || '').replace(new RegExp(TAG_SOURCE, 'g'), 'Tag')).length;
  }

  // Short, common acronyms and sizes are not shouting.
  const ACRONYMS = new Set(('SMS MMS USA US UK EU UAE NYC NY LA SF DC OK FAQ FAQS AM PM EST EDT PST PDT CST CDT MST MDT UTC GMT BST CET ' +
    'TV PC ID AI AR VR HD UHD LED LCD USB HDMI GPS PDF API URL DIY VIP BOGO BFCM RSVP ASAP FYI ETA TBD DM PS QR SKU UPS USPS DHL ' +
    'SPF UV UVA UVB CBD PH XS XL XXL XXXL NFL NBA MLB NHL FIFA CEO HR PR BBQ DNA IPA LGBTQ LGBTQIA ADHD FDA USDA NGO NFT CPU GPU ' +
    'RAM SSD II III IV VI VII VIII IX XI XII').split(' '));

  const lettersOf = word => (word.match(new RegExp(LETTERS.source, LETTERS.flags)) || []).join('');
  const hasCase = word => lettersOf(word).toUpperCase() !== lettersOf(word).toLowerCase();
  const isUpper = word => hasCase(word) && lettersOf(word) === lettersOf(word).toUpperCase();

  // Every word written in capitals, repeats included.
  function capsTokens(text) {
    return words(text).filter(word => {
      if (DIGIT.test(word)) return false; // codes such as WELCOME10 are meant to be typed as shown
      const letters = lettersOf(word);
      return letters.length >= 2 && isUpper(word) && !ACRONYMS.has(letters);
    });
  }

  const capsWords = text => Array.from(new Set(capsTokens(text)));

  function repeatedMarks(text) {
    const found = [];
    const pattern = /[!?]{2,}|([$€£¥*#~])\1+|\.{4,}|[‼⁉⁈⁇]/g;
    let match;
    while ((match = pattern.exec(String(text || '')))) if (!found.includes(match[0])) found.push(match[0]);
    return found;
  }

  const markCount = text => (String(text || '').match(/[!?！？‼⁉⁈⁇]/g) || []).length;

  const REPLY = /^\s*((?:(?:re|fwd?|fw)\s*(?:\[\d+\])?\s*:\s*)+)/i;

  function replyPrefix(subject) {
    const match = REPLY.exec(String(subject || ''));
    return match ? match[1].trim() : '';
  }

  const withoutReplyPrefix = subject => String(subject || '').replace(REPLY, '');

  function emojiList(text) {
    const found = [];
    graphemes(text).forEach(part => { if (EMOJI.test(part) && !found.includes(part)) found.push(part); });
    return found;
  }

  // Emoji plus text-style symbols: what a sender name shouldn't carry.
  function senderSymbols(text) {
    const found = [];
    graphemes(text).forEach(part => { if ((EMOJI.test(part) || SYMBOL.test(part)) && !found.includes(part)) found.push(part); });
    return found;
  }

  const normalWords = text => words(text).map(word => word.toLowerCase().replace(/’/g, "'"));

  // Does the preview repeat the subject instead of adding to it?
  function previewRepeatsSubject(subject, preview) {
    const subjectWords = normalWords(subject);
    const previewWords = normalWords(preview);
    if (!subjectWords.length || !previewWords.length) return null;
    const subjectLine = subjectWords.join(' ');
    const previewLine = previewWords.join(' ');
    if (subjectLine === previewLine) return { kind: 'same', shared: [] };
    if (previewWords.length > subjectWords.length && previewLine.startsWith(`${subjectLine} `)) return { kind: 'starts', shared: [] };
    const known = new Set(subjectWords);
    const unique = Array.from(new Set(previewWords));
    const shared = unique.filter(word => known.has(word));
    if (unique.length >= 3 && shared.length / unique.length >= 0.8) return { kind: 'mostly', shared };
    return null;
  }

  // ---- Laying out each inbox row ---------------------------------------------

  function initialOf(from) {
    const first = graphemes(collapse(from)).find(part => !isSpace(part));
    return first ? first.toUpperCase() : '';
  }

  function layoutClient(client, input, measure) {
    const fonts = client.fonts;
    const from = collapse(input.from);
    const subject = collapse(input.subject);
    const preview = collapse(input.preview);
    const time = input.time || TIME;
    const timeWidth = measure(time, fonts.time);
    const averageCharacter = measure(SAMPLE, fonts.preview) / SAMPLE.length;
    const roomCharacters = width => Math.floor(width / averageCharacter);
    const senderSpace = client.kind === 'line' ? client.senderWidth : client.textWidth - (client.timeOn === 'sender' ? timeWidth + GAP : 0);
    const senderFit = fitText(from, senderSpace, fonts.sender, measure);
    const sender = { text: senderFit.text, cut: senderFit.cut, empty: !from, shown: senderFit.shown, total: senderFit.count };
    const shownSubject = subject || client.noSubject;
    const previewTotal = countCharacters(preview);
    const layout = { id: client.id, name: client.name, short: client.short || client.name, phone: Boolean(client.phone), time, initial: initialOf(from), sender };

    if (client.kind === 'line') {
      const width = client.lineWidth;
      const fit = fitRuns([
        { text: shownSubject, font: fonts.subject },
        { text: client.separator, font: fonts.preview },
        { text: preview, font: fonts.preview }
      ], width, measure);
      const subjectCount = countCharacters(shownSubject);
      const separatorCount = countCharacters(client.separator);
      const subjectCut = fit.cut && fit.shown < subjectCount;
      const previewShown = Math.max(0, fit.shown - subjectCount - separatorCount);
      const previewHidden = Boolean(preview) && fit.cut && previewShown === 0;
      layout.line = { subject: fit.texts[0], separator: fit.texts[1], preview: fit.texts[2], ellipsisRun: fit.cut ? fit.ellipsisRun : -1, room: fit.room };
      layout.subject = { text: fit.texts[0], cut: subjectCut, shown: subjectCut ? fit.shown : subjectCount, total: subjectCount, empty: !subject };
      layout.preview = {
        lines: [{ text: fit.texts[2], cut: fit.cut && fit.ellipsisRun === 2 }], missing: !preview, hidden: previewHidden,
        cut: Boolean(preview) && fit.cut, shown: fit.cut ? previewShown : previewTotal, total: previewTotal,
        room: fit.room, roomChars: fit.cut ? 0 : roomCharacters(fit.room), lastRoom: fit.room, emptyLines: 0
      };
      return layout;
    }

    const subjectWidth = client.textWidth - (client.timeOn === 'subject' ? timeWidth + GAP : 0);
    const subjectFit = fitText(shownSubject, subjectWidth, fonts.subject, measure);
    layout.subject = { text: subjectFit.text, cut: subjectFit.cut, shown: subjectFit.shown, total: subjectFit.count, empty: !subject };
    const previewWidth = client.previewWidth || client.textWidth;
    const wrapped = wrapText(preview, previewWidth, client.previewLines, fonts.preview, measure);
    layout.preview = {
      lines: wrapped.lines, missing: !preview, hidden: false, cut: wrapped.cut, shown: wrapped.shown, total: previewTotal,
      room: preview ? wrapped.room : previewWidth * client.previewLines, roomChars: preview && !wrapped.cut ? roomCharacters(wrapped.room) : 0,
      lastRoom: wrapped.lastRoom, emptyLines: preview ? wrapped.emptyLines : client.previewLines, width: previewWidth
    };
    return layout;
  }

  function layoutAll(state, measure) {
    const mode = state.fill === 'empty' ? 'empty' : 'sample';
    const from = collapse(state.from);
    const input = {
      from,
      subject: collapse(fillTags(state.subject, mode, from)),
      preview: collapse(fillTags(state.preview, mode, from)),
      time: TIME
    };
    return CLIENTS.map(client => layoutClient(client, input, measure || approximateMeasure));
  }

  // ---- Checks ----------------------------------------------------------------

  const quote = text => `“${text}”`;
  const NO_SUBJECT = quote('(no subject)');

  // The app that shows the least of a line, and what it shows, quoted.
  const fewest = (list, part) => list.reduce((best, layout) => (layout[part].shown < best[part].shown ? layout : best));
  const shownText = (layout, part) => (part === 'subject' ? layout.subject.text : layout.preview.lines.map(line => line.text).join(' '));
  const cutQuote = (layout, part) => quote(`${shownText(layout, part)}${ELLIPSIS}`);
  const fillsRest = list => `${joinWords(list.map(layout => layout.short))} ${list.length === 1 ? 'fills' : 'fill'} the rest with text from your email.`;
  const noRoom = list => `${joinWords(list.map(layout => layout.short))} ${list.length === 1 ? 'has' : 'have'} no room for it after the subject.`;
  const blankWhen = fill => (fill === 'empty' ? 'with nothing on file' : 'once its tags are filled in');

  // ---- One-line answers under the fields ------------------------------------
  // Read where people type, often above a phone keyboard: one sentence each,
  // from the same layouts (tags filled in, spaces collapsed) as the checks.

  function subjectVerdict(state, layouts) {
    if (!collapse(state.subject)) return { status: 'warn', text: `No subject yet, so inboxes show ${NO_SUBJECT}.` };
    if (layouts.every(layout => layout.subject.empty)) return { status: 'warn', text: `Blank ${blankWhen(state.fill)}, so inboxes show ${NO_SUBJECT}.` };
    const cut = layouts.filter(layout => layout.subject.cut);
    if (!cut.length) return { status: 'ok', text: `Fits in all ${layouts.length} apps.` };
    const phones = layouts.filter(layout => layout.phone);
    const phonesCut = cut.filter(layout => layout.phone);
    if (!phonesCut.length) {
      const view = fewest(cut, 'subject');
      return { status: 'note', text: `Fits on phones. ${view.short} shows ${cutQuote(view, 'subject')}` };
    }
    const view = fewest(phonesCut, 'subject');
    return {
      status: phonesCut.length === phones.length ? 'warn' : 'note',
      text: `${view.short} shows ${cutQuote(view, 'subject')} (${number(view.subject.shown)} of ${plural(view.subject.total, 'character')})`
    };
  }

  function previewVerdict(state, layouts) {
    if (!collapse(state.preview)) return { status: 'warn', text: 'No preview text yet, so apps show the first text in your email.' };
    if (layouts.every(layout => layout.preview.missing)) return { status: 'warn', text: `Blank ${blankWhen(state.fill)}, so apps show the first text in your email.` };
    const phonesCut = layouts.filter(layout => layout.phone && layout.preview.cut);
    if (phonesCut.length) {
      const view = fewest(phonesCut, 'preview');
      return { status: 'note', text: `${view.short} shows ${cutQuote(view, 'preview')} (${number(view.preview.shown)} of ${plural(view.preview.total, 'character')})` };
    }
    const cut = layouts.filter(layout => layout.preview.cut && !layout.preview.hidden);
    if (cut.length) {
      const view = fewest(cut, 'preview');
      return { status: 'note', text: `Fits on phones. ${view.short} shows ${cutQuote(view, 'preview')}` };
    }
    const hidden = layouts.filter(layout => layout.preview.hidden);
    if (hidden.length) return { status: 'note', text: `Fits on phones. ${noRoom(hidden)}` };
    const roomy = layouts.filter(layout => layout.preview.roomChars >= ROOM_CHARS);
    if (roomy.length === layouts.length) return { status: 'warn', text: 'Short: every app fills the rest with text from your email.' };
    return { status: 'ok', text: `Fits in all ${layouts.length} apps.${roomy.length ? ` ${fillsRest(roomy)}` : ''}` };
  }

  function tagSummary(tags) {
    const raw = Array.from(new Set(tags.map(tag => tag.text)));
    return raw.length > 2 ? `${raw.slice(0, 2).join(', ')} and ${plural(raw.length - 2, 'more tag')}` : joinWords(raw);
  }

  // How a line reads for a reader with a value and for one with nothing on file.
  function versions(line, kind, from) {
    const withValue = collapse(fillTags(line, 'sample', from));
    const without = collapse(fillTags(line, 'empty', from));
    return { kind: 'versions', rows: [[`With a ${kind === 'detail' ? 'value' : kind}`, withValue], ['With nothing on file', without || '(blank)']] };
  }

  function runChecks(state, layouts) {
    const items = [];
    const passed = [];
    const from = collapse(state.from);
    const typedSubject = collapse(state.subject);
    const typedPreview = collapse(state.preview);
    const emptyMode = state.fill === 'empty';
    const subject = collapse(fillTags(state.subject, emptyMode ? 'empty' : 'sample', from));
    const preview = collapse(fillTags(state.preview, emptyMode ? 'empty' : 'sample', from));
    const sampleSubject = collapse(fillTags(state.subject, 'sample', from));
    const samplePreview = collapse(fillTags(state.preview, 'sample', from));
    const subjectTags = readerTags(state.subject);
    const previewTags = readerTags(state.preview);
    const tags = subjectTags.concat(previewTags);
    const add = (id, status, title, reason, action, extra) => items.push({ id, status, title, reason, action: action || '', extra: extra || null });
    const all = layouts.length;
    const filledNote = lineTags => (lineTags.length ? (emptyMode ? ' with tags left empty' : ` with ${quote(sampleValue(lineTags[0].field, from))} filled in`) : '');
    const blank = where => `${emptyMode ? 'With nothing on file, its tags are empty' : 'Once its tags are filled in, nothing is left'}, so ${where}.`;

    // Sender name.
    if (!from) {
      add('sender', 'warn', 'Add a sender name', 'It’s the first thing every app here shows, before the subject.', 'Use the name people know your store by.');
    } else {
      const symbols = senderSymbols(from);
      const senderCut = layouts.filter(layout => layout.sender.cut);
      if (symbols.length) {
        add('sender', 'warn', `Emoji or symbol in the sender name: ${symbols.slice(0, 4).join(' ')}`, 'Google’s sender guidelines say sender names shouldn’t include emoji or other characters that imitate graphics, such as a check mark that looks like a verified badge.', 'Use plain text for the name.');
      } else if (senderCut.length) {
        add('sender', 'warn', `Sender name cut off in ${joinWords(senderCut.map(layout => layout.name))}`, `Only ${quote(senderCut[0].sender.text + ELLIPSIS)} fits there.`, 'Use a shorter name, such as your store’s name on its own.');
      } else passed.push('sender name');
    }

    // Subject line. Outlook on a computer fits only about 33 characters, so it
    // cuts most subjects: the cut is worth fixing when both phone apps make it.
    if (!typedSubject) {
      add('subject', 'warn', 'Add a subject line', `Without one, inboxes show a placeholder such as ${NO_SUBJECT} instead.`, 'Say what the email is about in a few words.');
    } else if (!subject) {
      add('subject', 'warn', 'Subject would be blank', blank(`inboxes show ${NO_SUBJECT}`), 'Add words that don’t depend on a tag, or give the tag a fallback.');
    } else {
      const cut = layouts.filter(layout => layout.subject.cut);
      const size = `${plural(countCharacters(subject), 'character')}${filledNote(subjectTags)}, ${plural(countWords(subject), 'word')}`;
      if (!cut.length) {
        add('subject', 'ok', `Subject fits in all ${all} apps`, `${size}. Every app here shows the whole line.`, 'Bigger text sizes show less, so keep the key words first.');
      } else {
        const phones = layouts.filter(layout => layout.phone);
        const onPhones = phones.length > 0 && phones.every(layout => layout.subject.cut);
        const fitsPhones = !cut.some(layout => layout.phone);
        const view = fewest(cut, 'subject');
        add('subject', onPhones ? 'warn' : 'info', `Subject cut off in ${cut.length} of ${all} apps`,
          `${size}. ${fitsPhones ? 'It fits on phones. ' : ''}${view.short} shows ${cutQuote(view, 'subject')}`,
          onPhones ? 'Put the words that matter before the cut, or shorten it.' : 'Check that the words before the cut still make sense on their own.');
      }
    }

    // Preview text.
    if (!typedPreview) {
      add('preview', 'warn', 'Add preview text', `Without it, email apps show the first text they find in your email, often a ${quote('View in browser')} link or an image’s alt text.`, 'Fill in the preview text (preheader) field in your email app.');
    } else if (!preview) {
      add('preview', 'warn', 'Preview text would be blank', blank('apps show the first text in your email instead'), 'Add words that don’t depend on a tag, or give the tag a fallback.');
    } else {
      const repeat = previewRepeatsSubject(sampleSubject, samplePreview);
      if (repeat) {
        const reason = repeat.kind === 'same' ? 'Both lines say the same thing, so the preview adds nothing new.'
          : repeat.kind === 'starts' ? 'It starts by repeating the subject, which uses up the space where something new could go.'
            : `Most of its words are already in the subject: ${joinWords(repeat.shared.slice(0, 4).map(quote))}.`;
        add('repeat', 'warn', 'Preview text repeats the subject', reason, 'Use it for something the subject leaves out, such as the product, a detail or a date.');
      }
      const visible = layouts.filter(layout => !layout.preview.hidden);
      const roomy = visible.filter(layout => !layout.preview.cut && layout.preview.roomChars >= ROOM_CHARS);
      if (visible.length === all && roomy.length === all) {
        add('short', 'warn', 'Short preview text', 'Every app here has room left after it, and email apps fill that space with the first words inside your email.', 'Add a useful detail, and check what the first line of your email says.');
      } else {
        // The drawn rows carry each app's numbers; this names the cut that matters.
        const cut = layouts.filter(layout => layout.preview.cut && !layout.preview.hidden);
        const hidden = layouts.filter(layout => layout.preview.hidden);
        const phonesCut = cut.filter(layout => layout.phone);
        let detail = 'Every app here shows all of it.';
        if (cut.length) {
          const view = fewest(phonesCut.length ? phonesCut : cut, 'preview');
          detail = `${phonesCut.length ? '' : 'It fits on phones. '}${view.short} shows ${cutQuote(view, 'preview')}`;
        } else if (hidden.length) detail = noRoom(hidden);
        else if (roomy.length) detail = fillsRest(roomy);
        const size = `${plural(countCharacters(preview), 'character')}${filledNote(previewTags)}`;
        const title = cut.length + hidden.length ? `Preview text cut off in ${cut.length + hidden.length} of ${all} apps` : `Preview text shows in full in all ${all} apps`;
        add('preview', 'info', title, `${size}. ${detail}`, 'Put the most useful words first.');
      }
    }

    // Personalization tags. A store tag always has a value, and a tag inside a
    // condition is the condition's to handle, so neither needs a fallback.
    const open = tags.filter(tag => !tag.guarded);
    if (open.length) {
      const missing = open.filter(tag => !tag.fallback);
      const first = missing[0] || open[0];
      const kind = fieldKind(first.field);
      const extra = versions(subjectTags.includes(first) ? state.subject : state.preview, kind, from);
      if (missing.length) {
        const name = NAME_KINDS.has(kind);
        const mailchimp = missing.every(tag => tag.mailchimp);
        let action = name ? `Set a default word such as ${quote('there')} in your email app, or write the line so it works without it.` : 'Add a fallback in your email app, or drop the tag.';
        if (mailchimp) action = name ? `Set a default merge value such as ${quote('there')} in Mailchimp, or write the line so it works without it.` : 'Set a default merge value in Mailchimp, or drop the tag.';
        add('tags', 'warn', `No fallback for ${tagSummary(missing)}`,
          `Anyone with no ${kind} on file gets a blank where the tag is, as shown below${mailchimp ? ', unless you’ve set a default merge value in Mailchimp' : ''}.`, action, extra);
      } else {
        add('tags', 'ok', 'Personalization has a fallback', `Anyone with no ${kind} on file sees the fallback instead, as shown below.`, 'Read the fallback line once more: it should still sound natural.', extra);
      }
    }
    const logicLine = hasLogic(state.subject) ? state.subject : hasLogic(state.preview) ? state.preview : '';
    if (logicLine) {
      add('logic', 'info', 'Template logic isn’t run here', 'This page can’t run {% if %} or *|IF:|* conditions. With a sample name it shows the first part of each; with nothing on file, the else part.',
        'Check those lines in a test email.', open.length ? null : versions(logicLine, tags.length ? fieldKind(tags[0].field) : 'detail', from));
    }

    // Re: and Fwd:
    const prefix = replyPrefix(sampleSubject);
    if (prefix) {
      add('reply', 'warn', `Starts with ${quote(prefix)}`, 'It makes the email look like a reply to something the reader sent, and Google’s sender guidelines say not to use Re: or Fwd: unless the email really is one.', `Remove ${quote(prefix)}.`);
    } else if (typedSubject) passed.push('Re: or Fwd:');

    // Capitals. Campaign Monitor says capitals can work on a single word, so
    // this speaks up at two or more, or a subject all in capitals. A Re: or Fwd:
    // prefix has its own check above.
    const subjectBody = withoutReplyPrefix(sampleSubject);
    const capsSubject = capsTokens(subjectBody);
    const capsPreview = capsTokens(samplePreview);
    const cased = words(subjectBody).filter(word => !DIGIT.test(word) && hasCase(word));
    const whole = capsSubject.length > 0 && cased.length > 1 && cased.every(isUpper);
    if (whole || capsSubject.length > 1 || capsPreview.length > 1) {
      const caps = Array.from(new Set(capsSubject.concat(capsPreview)));
      const shownCaps = caps.slice(0, 4).join(', ') + (caps.length > 4 ? ` and ${plural(caps.length - 4, 'more')}` : '');
      add('caps', 'warn', whole ? 'Subject is all capitals' : `Capital letters: ${shownCaps}`, 'Campaign Monitor says capitals can work on a single word, but more can read as shouting.', 'Keep capitals to one word, unless it’s a code, a name or a short acronym.');
    } else if (typedSubject || typedPreview) passed.push('capital letters');

    // Repeated or piled-up marks. Campaign Monitor allows up to 3 exclamation
    // or question marks in a subject, so "!!" alone is only a note.
    const marks = Array.from(new Set(repeatedMarks(sampleSubject).concat(repeatedMarks(samplePreview))));
    const money = marks.filter(mark => /^[$€£¥]+$/.test(mark));
    const loud = marks.filter(mark => /^[!?‼⁉⁈⁇]+$/.test(mark));
    const other = marks.filter(mark => !money.includes(mark) && !loud.includes(mark));
    const subjectMarks = markCount(sampleSubject);
    if (money.length) {
      add('money', 'warn', `Repeated marks: ${money.slice(0, 3).map(quote).join(', ')}`, `${quote(money[0])} doesn’t say how much anyone saves.`, `Say the amount instead, like ${quote('Save $10')}.`);
    }
    if (subjectMarks > 3) {
      add('marks', 'warn', `${plural(subjectMarks, 'exclamation or question mark', 'exclamation and question marks')} in the subject`, 'Campaign Monitor suggests no more than 3 in a subject line.', 'Keep the one that matters most.');
    } else if (other.length) {
      add('marks', 'warn', `Repeated marks: ${other.slice(0, 4).map(quote).join(', ')}`, 'Marks in a row can look pushy.', 'Keep one, or none.');
    } else if (loud.length) {
      add('marks', 'info', `Repeated marks: ${loud.slice(0, 4).map(quote).join(', ')}`, 'Campaign Monitor suggests no more than 3 exclamation or question marks per subject line. One mark often says the same thing.', 'Keep one if the line reads just as well.');
    } else if (!money.length && (typedSubject || typedPreview)) passed.push('repeated marks');

    // Emoji.
    const emoji = emojiList(`${sampleSubject} ${samplePreview}`);
    if (emoji.length) {
      add('emoji', 'info', `Emoji: ${emoji.slice(0, 6).join(' ')}`, 'Emoji look different in each app, and screen readers read out their names.', 'Check them in a test email, and make sure the line still makes sense without them.', { kind: 'emoji', emoji: emoji.slice(0, 12) });
    }

    const order = { warn: 0, info: 1, ok: 2 };
    items.sort((a, b) => order[a.status] - order[b.status]);
    const warnings = items.filter(item => item.status === 'warn');
    // Read aloud, so it names at most three findings; the list has the rest.
    const named = warnings.slice(0, 3).map(item => item.title.replace(/[“”]/g, ''));
    if (warnings.length > 3) named.push(`${plural(warnings.length - 3, 'more')} in the list`);
    const summary = warnings.length ? `${plural(warnings.length, 'thing')} to check: ${named.join('; ')}.` : 'Nothing to fix in these checks.';
    return { items, passed, summary };
  }

  // ---- Markup (shared by the server render and the browser) ---------------

  const STAR = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.3 5.7.7-4.2 3.9 1.1 5.7L12 16.4l-5.1 2.8 1.1-5.7-4.2-3.9 5.7-.7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
  const cutMark = () => `<span class="slt-cut" aria-hidden="true">${ELLIPSIS}</span><span class="slt-visually-hidden"> (cut off here)</span>`;
  // The hidden space keeps "your cart." and the box's label apart for screen readers.
  const roomChip = (width, label, block) => `${block ? '' : '<span class="slt-visually-hidden"> </span>'}<span class="slt-room${block ? ' slt-room-line' : ''}" style="--room:${Math.max(0, Math.round(width))}"><span>${esc(label)}</span></span>`;
  const ROOM_LABEL = 'then text from your email';
  const MISSING_LABEL = 'first text in your email';

  function senderMarkup(layout, className) {
    if (layout.sender.empty) return `<span class="${className} slt-empty">No sender name</span>`;
    return `<span class="${className}">${esc(layout.sender.text)}${layout.sender.cut ? cutMark() : ''}</span>`;
  }

  function subjectMarkup(layout, className) {
    return `<span class="${className}${layout.subject.empty ? ' slt-no-subject' : ''}">${esc(layout.subject.text)}${layout.subject.cut ? cutMark() : ''}</span>`;
  }

  function previewLinesMarkup(layout, lineClass) {
    const preview = layout.preview;
    if (preview.missing) {
      return Array.from({ length: preview.emptyLines || 1 }, (_, index) => `<span class="${lineClass}">${roomChip(preview.width || preview.room, index ? '' : MISSING_LABEL, true)}</span>`).join('');
    }
    const showRoom = !preview.cut && preview.roomChars >= ROOM_CHARS;
    const lines = preview.lines.map((line, index) => {
      const last = index === preview.lines.length - 1;
      const chip = last && showRoom && preview.lastRoom >= 12 ? roomChip(preview.lastRoom, ROOM_LABEL) : '';
      return `<span class="${lineClass}">${esc(line.text)}${line.cut ? cutMark() : ''}${chip}</span>`;
    });
    if (showRoom) for (let index = 0; index < preview.emptyLines; index++) lines.push(`<span class="${lineClass}">${roomChip(preview.width, preview.lastRoom >= 12 ? '' : ROOM_LABEL, true)}</span>`);
    return lines.join('');
  }

  function rowMarkup(clientId, layout) {
    const time = esc(layout.time);
    const avatar = esc(layout.initial || '?');
    if (clientId === 'gmailWeb') {
      const line = layout.line;
      const preview = layout.preview;
      const subject = `<b class="slt-gw-subject${layout.subject.empty ? ' slt-no-subject' : ''}">${esc(line.subject)}${line.ellipsisRun === 0 ? cutMark() : ''}</b>`;
      let snippet = '';
      if (line.separator || line.preview) {
        snippet = `<span class="slt-gw-snippet">${esc(line.separator)}${esc(line.preview)}${line.ellipsisRun >= 1 ? cutMark() : ''}</span>`;
      }
      let chip = '';
      if (preview.missing && line.ellipsisRun === -1 && preview.room >= 12) chip = roomChip(preview.room, MISSING_LABEL);
      else if (!preview.missing && !preview.cut && preview.roomChars >= ROOM_CHARS) chip = roomChip(preview.room, ROOM_LABEL);
      return `<div class="slt-row slt-gw"><span class="slt-gw-box" aria-hidden="true"></span><span class="slt-gw-star">${STAR}</span>${senderMarkup(layout, 'slt-gw-from')}<span class="slt-gw-line">${subject}${snippet}${chip}</span><span class="slt-gw-time">${time}</span></div>`;
    }
    if (clientId === 'gmailApp') {
      return `<div class="slt-row slt-ga"><span class="slt-ga-avatar" aria-hidden="true">${avatar}</span><div class="slt-ga-text"><div class="slt-ga-top">${senderMarkup(layout, 'slt-ga-from')}<span class="slt-ga-time">${time}</span></div>${subjectMarkup(layout, 'slt-ga-subject')}<div class="slt-ga-bottom"><span class="slt-ga-snippet">${previewLinesMarkup(layout, 'slt-ga-line')}</span><span class="slt-ga-star">${STAR}</span></div></div></div>`;
    }
    if (clientId === 'appleMail') {
      return `<div class="slt-row slt-am"><span class="slt-am-dot" aria-hidden="true"></span><span class="slt-am-avatar" aria-hidden="true">${avatar}</span><div class="slt-am-text"><div class="slt-am-top">${senderMarkup(layout, 'slt-am-from')}<span class="slt-am-time">${time}</span></div>${subjectMarkup(layout, 'slt-am-subject')}<span class="slt-am-preview">${previewLinesMarkup(layout, 'slt-am-line')}</span></div></div>`;
    }
    return `<div class="slt-row slt-ol"><span class="slt-ol-bar" aria-hidden="true"></span><span class="slt-ol-avatar" aria-hidden="true">${avatar}</span><div class="slt-ol-text">${senderMarkup(layout, 'slt-ol-from')}<div class="slt-ol-mid">${subjectMarkup(layout, 'slt-ol-subject')}<span class="slt-ol-time">${time}</span></div><span class="slt-ol-preview">${previewLinesMarkup(layout, 'slt-ol-line')}</span></div></div>`;
  }

  function clientNote(layout) {
    const subject = layout.subject;
    const preview = layout.preview;
    const subjectPart = subject.empty ? `none, so it shows “${subject.text}”`
      : subject.cut ? `first ${number(subject.shown)} of ${plural(subject.total, 'character')}` : `all ${plural(subject.total, 'character')}`;
    let previewPart;
    if (preview.missing) previewPart = 'none, so it shows text from your email';
    else if (preview.hidden) previewPart = 'no room left after the subject';
    else if (preview.cut) previewPart = `first ${number(preview.shown)} of ${plural(preview.total, 'character')}`;
    else previewPart = `all ${plural(preview.total, 'character')}${preview.roomChars >= ROOM_CHARS ? ', then text from your email' : ''}`;
    return { subject: subjectPart, preview: previewPart };
  }

  function noteMarkup(layout) {
    const note = clientNote(layout);
    return `<span><b>Subject:</b> ${esc(note.subject)}</span><span><b>Preview:</b> ${esc(note.preview)}</span>`;
  }

  const STATUS = {
    warn: { icon: '!', label: 'Worth fixing: ' },
    info: { icon: 'i', label: 'Note: ' },
    ok: { icon: '✓', label: 'Looks fine: ' }
  };

  function checksMarkup(result) {
    const items = result.items.map(item => {
      const status = STATUS[item.status];
      let extra = '';
      if (item.extra && item.extra.kind === 'versions') {
        extra = `<dl class="slt-versions">${item.extra.rows.map(([label, text]) => `<div><dt>${esc(label)}</dt><dd>${esc(text)}</dd></div>`).join('')}</dl>`;
      } else if (item.extra && item.extra.kind === 'emoji') {
        extra = `<p class="slt-emoji-row" aria-hidden="true">${item.extra.emoji.map(char => `<span>${esc(char)}</span>`).join('')}</p>`;
      }
      return `<li class="slt-check" data-status="${item.status}" data-check="${esc(item.id)}"><span class="slt-check-icon" aria-hidden="true">${status.icon}</span><div class="slt-check-body"><p class="slt-check-title"><span class="slt-visually-hidden">${status.label}</span>${esc(item.title)}</p><p class="slt-check-reason">${esc(item.reason)}</p>${extra}${item.action ? `<p class="slt-check-action"><b>What to do:</b> ${esc(item.action)}</p>` : ''}</div></li>`;
    }).join('');
    return items;
  }

  function alsoText(result) {
    return result.passed.length ? `Also checked, nothing to fix: ${joinWords(result.passed)}.` : '';
  }

  // The toggle's words follow the tags: "Maya" only makes sense for a name.
  function fillLabels(state) {
    const tags = readerTags(state.subject).concat(readerTags(state.preview));
    const names = !tags.length || tags.some(tag => NAME_KINDS.has(fieldKind(tag.field)));
    return names ? { legend: 'Show name tags as', sample: 'Filled in (Maya)' } : { legend: 'Show tags as', sample: 'Filled in' };
  }

  function copyText(subject, preview) {
    return `Subject: ${collapse(subject)}\nPreview text: ${collapse(preview)}`;
  }

  // ---- Browser -----------------------------------------------------------------

  function init(root, options) {
    if (!root || root.dataset.sltReady) return null;
    const settings = options || {};
    const doc = root.ownerDocument;
    const view = doc.defaultView;
    const find = name => root.querySelector(`[data-slt-${name}]`);
    const fromInput = find('from');
    const subjectInput = find('subject');
    const previewInput = find('preview');
    if (!fromInput || !subjectInput || !previewInput) return null;
    root.dataset.sltReady = 'true';
    const browserMeasure = settings.measure ? null : canvasMeasure(doc);
    const measure = settings.measure || browserMeasure || approximateMeasure;
    root.dataset.sltMeasure = settings.measure ? 'custom' : browserMeasure ? 'canvas' : 'estimate';
    const fill = find('fill');
    const announcer = find('announcement');
    const status = find('status');
    let lastSummary = null;
    let announceTimer = null;
    let used = false;
    const announceDelay = Number.isFinite(settings.announceDelay) ? settings.announceDelay : 900;

    const toolEvent = name => doc.dispatchEvent(new view.CustomEvent('myuser:tool-event', { detail: { name } }));
    const fillMode = () => {
      if (!fill || fill.hidden) return 'sample';
      const checked = fill.querySelector('input:checked');
      return checked && checked.value === 'empty' ? 'empty' : 'sample';
    };
    const state = () => ({ from: fromInput.value, subject: subjectInput.value, preview: previewInput.value, fill: fillMode() });

    function markExamples() {
      root.querySelectorAll('[data-slt-example]').forEach(button => {
        const example = EXAMPLES[button.dataset.sltExample];
        const current = Boolean(example) && subjectInput.value === example.subject && previewInput.value === example.preview;
        button.setAttribute('aria-pressed', String(current));
      });
    }

    function showVerdict(name, verdict) {
      const line = find(`${name}-verdict`);
      if (!line) return;
      line.textContent = verdict.text;
      line.dataset.status = verdict.status;
    }

    function update(announce) {
      const current = state();
      const hasTags = readerTags(current.subject).length + readerTags(current.preview).length > 0;
      if (fill) {
        fill.hidden = !hasTags;
        if (!hasTags) current.fill = 'sample';
        const labels = fillLabels(current);
        const legend = find('fill-legend');
        const sample = find('fill-sample');
        if (legend && legend.textContent !== labels.legend) legend.textContent = labels.legend;
        if (sample && sample.textContent !== labels.sample) sample.textContent = labels.sample;
      }
      const layouts = layoutAll(current, measure);
      layouts.forEach(layout => {
        const figure = root.querySelector(`[data-slt-client="${layout.id}"]`);
        if (!figure) return;
        const screen = figure.querySelector('[data-slt-screen]');
        const note = figure.querySelector('[data-slt-note]');
        if (screen) screen.innerHTML = rowMarkup(layout.id, layout);
        if (note) note.innerHTML = noteMarkup(layout);
        figure.dataset.subjectCut = String(layout.subject.cut);
        figure.dataset.previewCut = String(layout.preview.cut);
      });
      const result = runChecks(current, layouts);
      const list = find('checks');
      if (list) list.innerHTML = checksMarkup(result);
      const also = find('also');
      if (also) { also.textContent = alsoText(result); also.hidden = !result.passed.length; }
      showVerdict('subject', subjectVerdict(current, layouts));
      showVerdict('preview', previewVerdict(current, layouts));
      root.dataset.sltMeasured = 'true';
      markExamples();
      if (!announce || !announcer) { lastSummary = lastSummary === null ? result.summary : lastSummary; return result; }
      // One short, polite update after typing pauses, and only when the list of
      // things to fix changes: a count read out on every keystroke is noise.
      view.clearTimeout(announceTimer);
      announceTimer = view.setTimeout(() => {
        if (result.summary === lastSummary) return;
        lastSummary = result.summary;
        announcer.textContent = `Checks updated. ${result.summary}`;
      }, announceDelay);
      return result;
    }

    function markUsed() {
      if (used) return;
      used = true;
      toolEvent('subject_tester_used');
    }

    function say(message) {
      if (status) status.textContent = message;
    }

    [fromInput, subjectInput, previewInput].forEach(input => input.addEventListener('input', () => { markUsed(); say(''); update(true); }));
    if (fill) fill.addEventListener('change', () => { markUsed(); update(true); });

    root.querySelectorAll('[data-slt-example]').forEach(button => {
      button.addEventListener('click', () => {
        const example = EXAMPLES[button.dataset.sltExample];
        if (!example) return;
        markUsed();
        subjectInput.value = example.subject;
        previewInput.value = example.preview;
        if (!collapse(fromInput.value)) fromInput.value = START.from;
        say(`${example.label} example loaded.`);
        update(true);
      });
    });

    const reset = find('reset');
    if (reset) reset.addEventListener('click', () => {
      const example = EXAMPLES[START.example];
      fromInput.value = START.from;
      subjectInput.value = example.subject;
      previewInput.value = example.preview;
      const sample = fill && fill.querySelector('input[value="sample"]');
      if (sample) sample.checked = true;
      say('Back to the starting example.');
      update(true);
    });

    const copy = find('copy');
    const fallback = find('copy-fallback');
    if (copy) copy.addEventListener('click', async () => {
      const text = copyText(subjectInput.value, previewInput.value);
      try {
        if (!view.navigator.clipboard || !view.navigator.clipboard.writeText) throw new Error('No clipboard');
        await view.navigator.clipboard.writeText(text);
        if (fallback) fallback.hidden = true;
        say('Copied both lines.');
        toolEvent('subject_tester_copied');
      } catch (_) {
        if (!fallback) { say('Copy isn’t available here. Select the lines and copy them.'); return; }
        const area = fallback.querySelector('textarea');
        fallback.hidden = false;
        area.value = text;
        area.focus();
        area.select();
        say('Copy isn’t available here. The text is selected: press Ctrl+C or ⌘C.');
      }
    });

    view.addEventListener('pagehide', () => view.clearTimeout(announceTimer));
    const result = update(false);
    return { update, state, result, measureKind: root.dataset.sltMeasure };
  }

  return Object.freeze({
    CLIENTS, EXAMPLES, START, TIME, ELLIPSIS, ROOM_CHARS,
    graphemes, countCharacters, countWords, collapse, esc,
    approximateMeasure, fitRuns, fitText, wrapText,
    findTags, readerTags, fillTags, resolveLogic, fieldKind, sampleValue, hasLogic,
    capsWords, repeatedMarks, replyPrefix, emojiList, senderSymbols, previewRepeatsSubject,
    layoutClient, layoutAll, runChecks, subjectVerdict, previewVerdict, fillLabels, clientNote,
    rowMarkup, noteMarkup, checksMarkup, alsoText, copyText, init
  });
});
